import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {
  QueryPaths,
  QueryPath,
  RuntimeInfo,
  ElementQuerySpecification,
} from '@packages/systems/dynamo/source-types';

import {
  isCommerceCheckoutFormContainer,
  isCommercePaypalCheckoutFormContainer,
  isCommerceOrderConfirmationContainer,
} from '@packages/utilities/elementTypeUtils';

import {WebflowEnvironments} from '@packages/systems/core-constants';

const COMMERCE_ORDER_ALIAS = 'orderConfirmationCommerceOrder';

export const ORDER_QUERY_SPECIFICATION: ElementQuerySpecification = {
  queryPathPrefix: specifyCommerceOrderWrapperQueryPathPrefix,
  boundaryElementQueryPaths: specifyCommerceOrderWrapperQueryPaths,
  hideQueryPathPrefix: true,
};

export const ORDER_ITEMS_QUERY_SPECIFICATION: ElementQuerySpecification = {
  queryPathPrefix: ['userItems'],
  relative: true,
};

function specifyCommerceOrderWrapperQueryPathPrefix(
  element: ExpressionElement,
  runtimeInfo: RuntimeInfo
): QueryPath {
  const commerceOrderQueryField = `commerceOrder${getCommerceOrderPartParams(
    element,
    runtimeInfo
  )}`;
  return [
    'database',
    isCommerceOrderConfirmationContainer(element)
      ? `${COMMERCE_ORDER_ALIAS}: ${commerceOrderQueryField}`
      : commerceOrderQueryField,
  ];
}

function specifyCommerceOrderWrapperQueryPaths(
  element: ExpressionElement,
  runtimeInfo: RuntimeInfo
): QueryPaths {
  const queryPathPrefix = specifyCommerceOrderWrapperQueryPathPrefix(
    element,
    runtimeInfo
  );

  const needsOrderCheckoutQueryPaths =
    isCommerceCheckoutFormContainer(element) ||
    isCommercePaypalCheckoutFormContainer(element) ||
    isCommerceOrderConfirmationContainer(element);

  const orderCheckoutQueryPaths: QueryPath[] = needsOrderCheckoutQueryPaths
    ? ORDER_CHECKOUT_QUERY_PATHS
    : [];

  const queryPaths: QueryPath[] = ORDER_BASE_QUERY_PATHS.concat(
    orderCheckoutQueryPaths
  ).map((qp) => queryPathPrefix.concat(qp));

  queryPaths.push(...SITE_QUERY_PATHS);

  return queryPaths;
}

function getCommerceOrderPartParams(
  element: ExpressionElement,
  runtimeInfo: RuntimeInfo
) {
  const {environment} = runtimeInfo;
  if (environment === WebflowEnvironments.DESIGNER) {
    return '(mockOrder: {maxItems: 20})';
  }

  if (
    environment === WebflowEnvironments.SSR &&
    isCommerceOrderConfirmationContainer(element)
  ) {
    return `(finalizedOrder: $finalizedOrder)`;
  }

  return '';
}

const SITE_QUERY_PATHS: QueryPath[] = [
  ['site', 'commerce', 'quickCheckoutEnabled'],
  ['site', 'commerce', 'defaultCurrency'],
  ['site', 'commerce', 'defaultCountry'],
  ['site', 'commerce', 'businessAddress', 'country'],
];

const ORDER_BASE_QUERY_PATHS: QueryPath[] = [
  // userItems default fields
  ['userItems', 'id'],
  ['userItems', 'count'],
  ['userItems', 'rowTotal'],
  ['userItems', 'rowTotal', 'string'],
  ['userItems', 'rowTotal', 'decimalValue'],
  ['userItems', 'rowTotal', 'unit'],
  ['userItems', 'rowTotal', 'value'],
  ['userItems', 'sku', 'id'],
  ['userItems', 'subscriptionInterval'],
  ['userItems', 'subscriptionFrequency'],
  ['userItems', 'subscriptionTrial'],

  ['id'],
  ['startedOn'],
  ['updatedOn'],
  ['comment'],
  ['userItemsCount'],

  ['extraItems', 'pluginName'],
  ['extraItems', 'pluginId'],
  ['extraItems', 'name'],
  ['extraItems', 'price'],
  ['extraItems', 'price', 'string'],
  ['extraItems', 'price', 'decimalValue'],
  ['extraItems', 'price', 'unit'],
  ['extraItems', 'price', 'value'],

  ['total'],
  ['total', 'string'],
  ['total', 'decimalValue'],
  ['total', 'unit'],
  ['total', 'value'],

  ['subtotal'],
  ['subtotal', 'string'],
  ['subtotal', 'decimalValue'],
  ['subtotal', 'unit'],
  ['subtotal', 'value'],
  ['statusFlags', 'requiresShipping'],
  ['statusFlags', 'hasDownloads'],
  ['statusFlags', 'isFreeOrder'],
  ['statusFlags', 'hasSubscription'],
];

export const ORDER_CHECKOUT_QUERY_PATHS: QueryPath[] = [
  ['availableShippingMethods', 'id'],
  ['availableShippingMethods', 'name'],
  ['availableShippingMethods', 'description'],
  ['availableShippingMethods', 'mode'],
  ['availableShippingMethods', 'selected'],
  ['availableShippingMethods', 'price'],
  ['availableShippingMethods', 'price', 'string'],
  ['availableShippingMethods', 'price', 'decimalValue'],
  ['availableShippingMethods', 'price', 'unit'],
  ['availableShippingMethods', 'price', 'value'],

  ['customerInfo', 'stripePayment', 'card', 'provided'],
  ['customerInfo', 'stripePayment', 'card', 'last4'],
  ['customerInfo', 'stripePayment', 'card', 'provider'],
  ['customerInfo', 'stripePayment', 'card', 'expires', 'month'],
  ['customerInfo', 'stripePayment', 'card', 'expires', 'year'],

  ['customerInfo', 'identity', 'fullName'],
  ['customerInfo', 'identity', 'email'],

  ['customerInfo', 'shippingAddress', 'type'],
  ['customerInfo', 'shippingAddress', 'addressee'],
  ['customerInfo', 'shippingAddress', 'japanType'],
  ['customerInfo', 'shippingAddress', 'line1'],
  ['customerInfo', 'shippingAddress', 'line2'],
  ['customerInfo', 'shippingAddress', 'city'],
  ['customerInfo', 'shippingAddress', 'state'],
  ['customerInfo', 'shippingAddress', 'country'],
  ['customerInfo', 'shippingAddress', 'postalCode'],

  ['customerInfo', 'billingAddress', 'type'],
  ['customerInfo', 'billingAddress', 'addressee'],
  ['customerInfo', 'billingAddress', 'japanType'],
  ['customerInfo', 'billingAddress', 'line1'],
  ['customerInfo', 'billingAddress', 'line2'],
  ['customerInfo', 'billingAddress', 'city'],
  ['customerInfo', 'billingAddress', 'state'],
  ['customerInfo', 'billingAddress', 'country'],
  ['customerInfo', 'billingAddress', 'postalCode'],

  ['statusFlags', 'needItems'],
  ['statusFlags', 'needIdentity'],
  ['statusFlags', 'needAddress'],
  ['statusFlags', 'needPayment'],
  ['statusFlags', 'shouldRecalc'],
  ['statusFlags', 'shippingAddressRequiresPostalCode'],
  ['statusFlags', 'billingAddressRequiresPostalCode'],

  ['customData', 'name'],
  ['customData', 'textArea'],
  ['customData', 'textInput'],
  ['customData', 'checkbox'],

  ['paymentProcessor'],

  ['downloadFiles', 'name'],
  ['downloadFiles', 'url'],
];
